import { Cog6ToothIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useSession } from "../../session/sessionContext";
import { isAllowed } from "../../api/access/decisions";
import { sessionDecision } from "../../session/sessionMachine";
import { appPaths } from "../../shared/routing/appPaths";
import AppButton from "../../ui/buttons/AppButton";
import Chip from "../../ui/feedback/Chip";
import EmptyState from "../../ui/feedback/EmptyState";
import LoadStatus from "../../ui/feedback/LoadStatus";
import { classNames } from "../../ui/styles/classNames";
import NewProjectDialog from "../create/NewProjectDialog";
import { useProjects } from "../projectContext";
import ProjectBasis from "./ProjectBasis";

/**
 * `/projects`, "All projects…" in the project menu (web-app.md WA-5): the projects the user
 * can see, each with its settings, and a new project where the session permits
 * `create_project`. Members, rules and history are in each project's settings. The DMW core
 * is greyed out and only administrators open it (F62); each project shows that it inherits
 * the DMW basis (F63).
 */
export default function AllProjectsPage() {
  const { t } = useTranslation();
  const { session } = useSession();
  const { projects, selectedProjectId, selectProject, mayChoose, reloadProjects } = useProjects();
  const [creating, setCreating] = useState(false);
  const isAdministrator = session.status === "signedIn" && session.session.account.accountRole === "administrator";
  const createDecision = sessionDecision(session, "create_project");

  return (
    <div className="max-w-2xl space-y-6 p-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className="app-title text-xl font-bold">{t("projects.title")}</h2>
        <AppButton variant="primary" size="sm" requires={createDecision} onClick={() => { setCreating(true); }}>{t("projects.menu.newProject")}</AppButton>
      </div>
      <LoadStatus state={projects} loadingLabel={t("common.loading")} onRetry={reloadProjects} />
      {projects.status === "loaded" && projects.value.length === 0 ? (
        <EmptyState>{t(emptyTextKey(isAdministrator, isAllowed(createDecision)))}</EmptyState>
      ) : null}
      {projects.status === "loaded" && projects.value.length > 0 ? (
        <ul className="space-y-1">
          {projects.value.map((project) => {
            const chosen = project.projectId === selectedProjectId;
            const choosable = mayChoose(project);
            return (
              <li key={project.projectId} className={classNames("flex items-center gap-1", project.installationOwned && "opacity-60")}>
                <button
                  type="button"
                  className={classNames("app-nav-item flex min-w-0 flex-1 items-center gap-2 px-3 py-2 text-left text-sm font-semibold disabled:cursor-not-allowed", chosen && "app-nav-item-active")}
                  aria-pressed={chosen}
                  disabled={!choosable}
                  onClick={() => { selectProject(project.projectId); }}
                >
                  <span className="truncate">{project.name}</span>
                  <ProjectBasis project={project} />
                  {chosen ? <Chip className="ml-auto shrink-0 font-normal">{t("projects.chosen")}</Chip> : null}
                </button>
                {choosable ? (
                  <Link
                    to={appPaths.projectSettings(project.projectId)}
                    className="app-nav-item shrink-0 p-2"
                    aria-label={t("projects.menu.settingsOf", { project: project.name })}
                    title={t("projects.menu.settingsOf", { project: project.name })}
                  >
                    <Cog6ToothIcon className="h-4 w-4" aria-hidden="true" />
                  </Link>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
      {creating ? <NewProjectDialog onClose={() => { setCreating(false); }} /> : null}
    </div>
  );
}

function emptyTextKey(isAdministrator: boolean, mayCreateProjects: boolean): string {
  if (isAdministrator) {
    return "projects.emptyForAdministrator";
  }
  return mayCreateProjects ? "projects.emptyForProjectCreator" : "projects.emptyForMember";
}
